// Local-calendar date keys.
//
// `new Date().toISOString().slice(0, 10)` is the wrong tool for keying a Postgres
// `date` column. Applied to a locally-normalised Date — e.g. local midnight from
// `setHours(0,0,0,0)` — it converts to UTC first, so in IST (UTC+5:30) local
// midnight is 18:30Z the PREVIOUS day and the key comes back one day early.
// Every consumer then reads the wrong row: PeopleHRScreen's week window fetched
// Sun-Sat instead of Mon-Sun, its attendance strip rendered Sunday's status under
// Monday, and every heatmap cell showed the previous day.
//
// The server stamps `attendance.date` from `org_today()`, which is genuinely
// org-local, so the two disagreed by exactly one day for every user east of GMT.
//
// Use `isoDay` whenever the value is compared against a `date` column.
//
// The org's calendar, not the browser's, is the app's calendar. `orgDateKey` and
// `orgToday` below are the org-timezone equivalents, and they are the default
// basis for anything that means "a day" or "a week" — `attendance.date` is
// stamped by SQL `org_today()`, so a browser-local key disagrees with the server
// for every viewer outside the org's zone.
//
// See docs/UI-CONVENTIONS.md §2 for the rule these implement.

/** 'YYYY-MM-DD' from a Date's LOCAL calendar fields — never via UTC. */
export const isoDay = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Today, as a local-calendar key. */
export const todayIso = (): string => isoDay(new Date());

/** A new Date `n` days from `d`, without mutating it. */
export const addDays = (d: Date, n: number): Date => {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
};

/** The 'YYYY-MM-DD' key `n` days from another key. Parsed at local midnight, never UTC. */
export const addDaysToKey = (key: string, n: number): string =>
  isoDay(addDays(new Date(`${key}T00:00:00`), n));

// ── The org's calendar ───────────────────────────────────────────────────────

/** Calendar date of an INSTANT in a given IANA zone. 'en-CA' formats as ISO. */
export const orgDateKey = (t: string | number | Date, tz: string): string => {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date(t));
  } catch {
    // Unknown zone (a bad tz_iana) — the browser's calendar is a better answer
    // than a crash, and it is what the app did everywhere before this.
    return isoDay(new Date(t));
  }
};

/** 'YYYY-MM-DDTHH:mm' — an instant on the org's wall clock, for a datetime-local input. */
export const toOrgWallTime = (iso: string, tz: string): string => {
  try {
    const p = Object.fromEntries(
      new Intl.DateTimeFormat('en-CA', {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      })
        .formatToParts(new Date(iso))
        .map((x) => [x.type, x.value]),
    );
    return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
  } catch {
    // Unknown zone — the browser's clock, as orgDateKey falls back to its calendar.
    const d = new Date(iso);
    return `${isoDay(d)}T${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }
};

/** A clock time on the org's clock, e.g. "14:30". */
export const orgTime = (iso: string, tz: string): string => {
  const hm = { hour: '2-digit', minute: '2-digit' } as const;
  try {
    return new Date(iso).toLocaleTimeString(undefined, { ...hm, timeZone: tz });
  } catch {
    // Unknown zone — the browser's clock rather than a crash, as orgDateKey does.
    return new Date(iso).toLocaleTimeString(undefined, hm);
  }
};

/**
 * A time of day, given as minutes after midnight: "14:30", or "02:30 PM" where
 * the browser's language counts twelve hours. orgTime's options, so a setting
 * and a timestamp read alike. Org's Work time axis drew 24-hour times under
 * 12-hour inputs (critique 2026-10-06 #6). It follows the browser's language,
 * which a native `<input type="time">` need not: Chrome on Windows and macOS
 * draws that in the OS region's format, so the two can still disagree
 * (UI-CONVENTIONS Deferred).
 */
export const fmtTimeOfDay = (mins: number): string =>
  new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' }).format(
    new Date(2000, 0, 1, Math.floor(mins / 60), mins % 60),
  );

/**
 * Today in the ORG's calendar, as a local-midnight Date — the client's
 * `org_today()`. Its getFullYear/getMonth/getDate are the org's, so every other
 * helper here (isoDay, addDays, weekStart) then works on the right calendar day
 * no matter where the browser is.
 */
export const orgToday = (tz: string): Date => new Date(`${orgDateKey(Date.now(), tz)}T00:00:00`);

// ── Weeks ────────────────────────────────────────────────────────────────────

/** Labels indexed by getDay() (0=Sun … 6=Sat), so they cannot be mis-rotated. */
export const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'] as const;

/**
 * org_settings.week_start ('Sun'/'Mon'/…) → a getDay() index.
 *
 * Defaults to 0 = Sunday, which is the COLUMN's own default
 * (20260604010000_modules.sql:157 `week_start text not null default 'Sun'`).
 * It used to default to Monday, so a fresh org — or any failed settings read —
 * was Sunday-configured and Monday-rendered, which is the bug itself.
 */
export const weekStartIndex = (s?: string | null): number =>
  ({ sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 })[
    (s ?? '').slice(0, 3).toLowerCase()
  ] ?? 0;

/** Seven labels, the first being `weekStartsOn`. The only way to render a header row. */
export const weekdayLabels = (weekStartsOn: number): string[] =>
  Array.from({ length: 7 }, (_, i) => WEEKDAY_LABELS[(weekStartsOn + i) % 7]);

/** Local midnight of the week containing `base`, anchored on `weekStartsOn`. */
export const weekStart = (weekStartsOn: number, base = new Date(), offsetWeeks = 0): Date => {
  const d = new Date(base);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - ((d.getDay() - weekStartsOn + 7) % 7) + offsetWeeks * 7);
  return d;
};

/** The seven 'YYYY-MM-DD' keys of that week, in grid-column order. */
export const weekKeys = (weekStartsOn: number, base = new Date(), offsetWeeks = 0): string[] => {
  const s = weekStart(weekStartsOn, base, offsetWeeks);
  return Array.from({ length: 7 }, (_, i) => isoDay(addDays(s, i)));
};

/**
 * Grid column 0..6 for a date key. Parses at local midnight — `new Date('2026-08-09')`
 * alone is parsed as UTC and yields the previous day west of GMT.
 *
 * Bucket by calendar key, never by `Math.floor(delta / 86400000)`: a ms delta is
 * wrong on the 23- and 25-hour days of a DST transition, and the two screens
 * doing it disagreed anyway (one floored, one rounded).
 */
export const dayIndex = (iso: string, weekStartsOn: number): number =>
  (new Date(`${iso}T00:00:00`).getDay() - weekStartsOn + 7) % 7;

// ── Months ───────────────────────────────────────────────────────────────────

/**
 * First and last calendar day of the month containing `base`, inclusive.
 *
 * Pass `orgToday(tz)` as the base — it already returns a local-midnight Date whose
 * year/month/day are the ORG's, so this stays on the org calendar without
 * inventing a second timezone concept. That is what `orgToday` returning a Date
 * is for.
 *
 * Built from explicit year/month fields, never `setMonth()`: `setMonth()` applied
 * to the 31st overflows into the following month (Aug 31 → "Feb 31" → Mar 3), so
 * stepping back through months from the end of a long one silently skips.
 * Day 0 of month n+1 is the last day of month n, leap years included.
 */
export const monthRange = (base: Date, offsetMonths = 0): { from: string; to: string } => {
  const first = new Date(base.getFullYear(), base.getMonth() + offsetMonths, 1);
  return {
    from: isoDay(first),
    to: isoDay(new Date(first.getFullYear(), first.getMonth() + 1, 0)),
  };
};

/** dateKey → column, for bucketing rows into a week grid. `undefined` = outside it. */
export const weekColumns = (
  weekStartsOn: number,
  base = new Date(),
  offsetWeeks = 0,
): Map<string, number> => new Map(weekKeys(weekStartsOn, base, offsetWeeks).map((k, i) => [k, i]));

// ── Display ──────────────────────────────────────────────────────────────────
//
// About ten ad-hoc formatters grew with no shared one (ME-13, HRP-13): raw ISO,
// two hand-built DD-MM-YYYY copies, bare toLocaleDateString() in the browser's
// locale, "Oct 12" beside "01-03-2022" on one page. One of them parsed a
// date-only key as UTC, so HR west of GMT saw every leave request a day early
// (HRA-V2). These are the display half of §2; everything above is the boundary
// half.
//
//   fmtDate     a full date, in the org's date_format      → "27-09-2026"
//   fmtDayKey   a compact label: month spelled, no year    → "27 Sep", "Sun 27 Sep"
//   fmtDayLong  a day said in full, as a control's name    → "Sunday 27 September 2026"
//   fmtMonth    a month by name                            → "September 2026"
//   monthAbbr   a key's month alone (weekdayAbbr: weekday) → "Sep", "Sun"
//   fmtRelative a timestamp relative to now                → "5 minutes ago"
//   weekHeader  the caption over a week grid               → "Week of 27 Sep"
//   tzLabel     an IANA zone with its offset AT an instant → "America/New_York · EDT (GMT−4)"
//
// Month and weekday names are fixed English, not the browser's locale: the UI
// is English, and a date that renders differently on two HR machines is part of
// what this replaces. fmtDayKey ignores date_format on purpose — it spells the
// month, so "27 Sep" cannot be misread under any format; the numeric ambiguity
// the setting exists for (03/07) only arises in fmtDate.
//
// In a component, read the format once, with the rest of the org's settings:
//
//   const { dateFormat } = useOrgDates();  // the column default until the read lands
//   fmtDate(row.joined, dateFormat)
//
// `null` prints the column default, not the org's choice: it is for code with
// no setting to hand. The calls that still pass it are in the Deferred table.
//
// See docs/UI-CONVENTIONS.md §2, "Displaying a date", and its Deferred table.

const MINUS = '−';
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;
/** getDay()-indexed, like WEEKDAY_LABELS, so it cannot be mis-rotated. */
const DAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;
// "Sep", "Thu": the first three letters of every English month and day name.
const MONTH_ABBR = MONTH_NAMES.map((m) => m.slice(0, 3));
const DAY_ABBR = DAY_NAMES.map((d) => d.slice(0, 3));
const DAY_KEY = /^\d{4}-\d{2}-\d{2}$/;
const pad2 = (n: number) => String(n).padStart(2, '0');

/** org_settings.date_format — exactly the four options Org → Office hours offers. */
export const DATE_FORMATS = ['DD-MM-YYYY', 'DD MMM YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'] as const;
export type DateFormat = (typeof DATE_FORMATS)[number];

/**
 * The COLUMN default, not a preference (UI-CONVENTIONS §2 rule 2):
 * 20260604010000_modules.sql:158 `date_format text not null default 'DD-MM-YYYY'`.
 * A missing, failed or unrecognised setting renders what a fresh org renders.
 */
export const DEFAULT_DATE_FORMAT: DateFormat = 'DD-MM-YYYY';

/**
 * A calendar day as a local-midnight Date. A 'YYYY-MM-DD' key is parsed at
 * LOCAL midnight — `new Date('2026-06-18')` is UTC midnight, which is 17 June
 * west of GMT (HRA-V2). An instant becomes the org's calendar day when `tz` is
 * given, else the viewer's.
 */
const calendarDay = (input: string | Date, tz?: string): Date => {
  if (typeof input === 'string' && DAY_KEY.test(input)) return new Date(`${input}T00:00:00`);
  return tz ? new Date(`${orgDateKey(input, tz)}T00:00:00`) : new Date(input);
};

/**
 * A full date in the org's `date_format` — the first reader of that setting.
 *
 * `dateFormat` is positional and required so no call site can forget it; pass
 * `null` where it isn't loaded and the column default renders. `tz` puts an
 * instant (a timestamptz) on the org's calendar; a date key needs none.
 * Unparseable input renders "—", never "NaN-NaN-NaN".
 */
export const fmtDate = (
  input: string | Date,
  dateFormat: string | null | undefined,
  opts: { tz?: string; weekday?: boolean } = {},
): string => {
  const d = calendarDay(input, opts.tz);
  if (Number.isNaN(d.getTime())) return '—';
  const f = (DATE_FORMATS as readonly string[]).includes(dateFormat ?? '')
    ? (dateFormat as DateFormat)
    : DEFAULT_DATE_FORMAT;
  const dd = pad2(d.getDate());
  const mm = pad2(d.getMonth() + 1);
  const y = d.getFullYear();
  const body = {
    'DD-MM-YYYY': `${dd}-${mm}-${y}`,
    'DD MMM YYYY': `${dd} ${MONTH_ABBR[d.getMonth()]} ${y}`,
    'MM/DD/YYYY': `${mm}/${dd}/${y}`,
    'YYYY-MM-DD': `${y}-${mm}-${dd}`,
  }[f];
  return opts.weekday ? `${DAY_ABBR[d.getDay()]}, ${body}` : body;
};

/**
 * A 'YYYY-MM-DD' key as a compact label — "27 Sep", or "Sun 27 Sep" with
 * `weekday`. For grids, lists and ranges where the year is context. Keys only:
 * anything else renders "—" rather than being guessed at.
 */
export const fmtDayKey = (key: string, opts: { weekday?: boolean } = {}): string => {
  const d = new Date(`${key}T00:00:00`);
  if (!DAY_KEY.test(key) || Number.isNaN(d.getTime())) return '—';
  const label = `${d.getDate()} ${MONTH_ABBR[d.getMonth()]}`;
  return opts.weekday ? `${DAY_ABBR[d.getDay()]} ${label}` : label;
};

/**
 * A span of days as a reader checks it before submitting: "Mon 12 Oct – Wed
 * 14 Oct", or the one day alone. The native date field shows the browser's
 * order (mm/dd/yyyy beside the org's DD-MM-YYYY), so a form echoes what was
 * picked, with the length it already counts: "… · 3 working days" (critique
 * 2026-10-05 #2). An en dash, as a range is drawn elsewhere; "→" is a change.
 * Empty without a start, or with an end before it, so the echo never states a
 * range the form is about to refuse.
 *
 * The year shows when the reader can't assume it, as in fmtRelative: on both
 * ends when the span crosses one ("Wed 30 Dec 2026 – Fri 1 Jan 2027"), and
 * after the last day when it isn't `today`'s year. Without it, a leave picked
 * a year out read like this year's and passed the check unseen. `today` is a
 * 'YYYY-MM-DD' key; pass the org's (orgDateKey).
 */
export const fmtDayRange = (from: string, to?: string, today = isoDay(new Date())): string => {
  if (!DAY_KEY.test(from)) return '';
  const end = !to || to === from ? from : DAY_KEY.test(to) && to > from ? to : '';
  if (!end) return '';
  const day = (k: string, year: boolean) =>
    `${fmtDayKey(k, { weekday: true })}${year ? ` ${k.slice(0, 4)}` : ''}`;
  const crosses = from.slice(0, 4) !== end.slice(0, 4);
  const away = end.slice(0, 4) !== today.slice(0, 4);
  return end === from ? day(from, away) : `${day(from, crosses)} – ${day(end, crosses || away)}`;
};

/** "Sep" and "Thu" — a key's month and weekday on their own, for a date tile
 *  that sets the day and the month apart. Read from the key, never cut out of
 *  another helper's label. Keys only; anything else is "—". */
export const monthAbbr = (key: string): string => {
  const d = new Date(`${key}T00:00:00`);
  return !DAY_KEY.test(key) || Number.isNaN(d.getTime()) ? '—' : MONTH_ABBR[d.getMonth()];
};
export const weekdayAbbr = (key: string): string => {
  const d = new Date(`${key}T00:00:00`);
  return !DAY_KEY.test(key) || Number.isNaN(d.getTime()) ? '—' : DAY_ABBR[d.getDay()];
};

/** "October 2026" — a month by name. `month` is 0–11, as Date's. */
export const fmtMonth = (year: number, month: number): string => `${MONTH_NAMES[month]} ${year}`;

/**
 * "Thursday 1 October 2026" — a day said in full, for where it is a control's
 * name and nothing on screen spells it out (a MonthGrid day cell). Keys only,
 * like fmtDayKey; anything else is "—".
 */
export const fmtDayLong = (key: string): string => {
  const d = new Date(`${key}T00:00:00`);
  if (!DAY_KEY.test(key) || Number.isNaN(d.getTime())) return '—';
  return `${DAY_NAMES[d.getDay()]} ${d.getDate()} ${fmtMonth(d.getFullYear(), d.getMonth())}`;
};

const RELATIVE = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

/** Whole calendar days from a to b — via Date.UTC, so a 23-hour DST day is still one day. */
const calendarDaysBetween = (a: Date, b: Date): number =>
  Math.round(
    (Date.UTC(b.getFullYear(), b.getMonth(), b.getDate()) -
      Date.UTC(a.getFullYear(), a.getMonth(), a.getDate())) /
      86_400_000,
  );

/**
 * A timestamp relative to `now`, in the viewer's zone (§2: a notification time
 * is the viewer's; only a calendar DAY is the org's). No seconds (WS-15): under
 * a minute is "just now"; under a day counts minutes or hours; then calendar
 * days ("yesterday", "3 days ago"), counted by calendar and not by 24-hour
 * blocks, so Saturday evening seen on Monday morning is "2 days ago" whatever
 * the hours. A week or more is a date ("12 Sep", with the year when it isn't
 * this one).
 */
export const fmtRelative = (
  instant: string | number | Date,
  now: number | Date = Date.now(),
): string => {
  const t = new Date(instant);
  const n = new Date(now);
  if (Number.isNaN(t.getTime())) return '—';
  const sec = (t.getTime() - n.getTime()) / 1000;
  const abs = Math.abs(sec);
  if (abs < 60) return 'just now';
  if (abs < 3600) return RELATIVE.format(Math.trunc(sec / 60), 'minute');
  if (abs < 86_400) return RELATIVE.format(Math.trunc(sec / 3600), 'hour');
  const days = calendarDaysBetween(n, t);
  if (Math.abs(days) < 7) return RELATIVE.format(days, 'day');
  const label = fmtDayKey(isoDay(t));
  return t.getFullYear() === n.getFullYear() ? label : `${label} ${t.getFullYear()}`;
};

/**
 * "Week of 27 Sep" — the caption over a week grid (TS-12: the grid never said
 * which dates its columns were). `day` is any day in the week — the grid's own
 * `weekStart(...)`, or today — and is aligned to `weekStartsOn`, so the caption
 * cannot name a day the grid doesn't start on.
 */
export const weekHeader = (day: Date, weekStartsOn: number): string =>
  `Week of ${fmtDayKey(isoDay(weekStart(weekStartsOn, day)))}`;

// Locales whose CLDR data names a zone's abbreviation; every other locale prints
// the "GMT+8" fallback, which says nothing the offset doesn't.
// ponytail: an abbreviation shows only where one of these names it (US zones,
// IST, GST, BST/CET/CEST, AEST/NZST); elsewhere the offset alone, which is always
// right. Add a locale here if a studio's zone deserves its abbreviation.
const ABBR_LOCALES = ['en-US', 'en-GB', 'en-IN', 'en-AU'] as const;

const zoneName = (locale: string, zone: string, at: Date, style: 'short' | 'shortOffset') =>
  new Intl.DateTimeFormat(locale, { timeZone: zone, timeZoneName: style })
    .formatToParts(at)
    .find((p) => p.type === 'timeZoneName')?.value ?? '';

/**
 * An IANA zone labelled with the abbreviation and UTC offset IN EFFECT AT `at`:
 * "America/New_York · EST (GMT−5)" in January, "· EDT (GMT−4)" in July. The Org
 * picker's five literal labels stated the winter offset all year (ORG-14).
 * Offsets take U+2212, like the labels this replaces. An unknown zone comes back
 * as given rather than throwing.
 */
export const tzLabel = (zone: string, at: Date = new Date()): string => {
  try {
    // A zero offset is "GMT" in current ICU but "GMT+0" in older builds (Node
    // 20, older browsers): one label everywhere, and "GMT (GMT+0)" never shows.
    const offset = zoneName('en-US', zone, at, 'shortOffset')
      .replace(/^GMT[+-]0(:00)?$/, 'GMT')
      .replace('-', MINUS);
    const abbr = ABBR_LOCALES.map((l) => zoneName(l, zone, at, 'short')).find(
      (s) => s && !/^GMT[+-]/.test(s),
    );
    return abbr && abbr !== offset && abbr !== zone
      ? `${zone} · ${abbr} (${offset})`
      : `${zone} · ${offset}`;
  } catch {
    return zone;
  }
};
